import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import ClassNames from 'classnames';
import '@/assets/styles/components/whiteboard-wv.styles.scss';

import { useFeatureFlag } from 'configcat-react';
import LoadingSpinner from '@/components/loading-spinner';
import { useSizes } from '@/utility/responsive.ts';
import { jsonTryParse } from '@/utility';

export const MeetVWOutActions = {
	initialize: 'initialize',
	restoreData: 'restoreData'
};

export const MeetVWInActions = {
	initialized: 'initialized',
	onWhiteBoardData: 'onWhiteBoardData',
	onClearClick: 'onClearClick'
};

export const SolverActions = {
	setQuestionImage: 'setQuestionImage'
};

type Props = {
	streamId: string | null;
	onWhiteBoardData: (data: any) => void;
	whiteboardLastData: any;
	isSolver?: boolean;
	onClearClick?: () => void;
};

const WhiteBoardWv = forwardRef(({ streamId, onWhiteBoardData, whiteboardLastData, isSolver = false, onClearClick }: Props, ref) => {
	const [isLoad, setIsLoad] = useState(true);
	const [url, setUrl] = useState('');
	const [whiteBoardData, setWhiteBoardData] = useState(null);
	const [isInitialized, setIsInitialized] = useState(false);

	const { isMobile } = useSizes();

	const loadEndTimeoutRef = useRef();
	const iframeRef = useRef(null);

	const dopingTechComponentsCacheStrRef = useRef('current');
	const { value: dopingTechComponentsCacheStr } = useFeatureFlag('dopingtechcomponentsCacheStr', 'current');

	const onMessage = (event: any) => {
		const data = event?.data;

		if (data?.type === MeetVWInActions.initialized) {
			setIsLoad(false);
			sendMessage({
				type: MeetVWOutActions.restoreData,
				data: whiteboardLastData
			});
			setIsInitialized(true);
		} else if (data?.type === MeetVWInActions.onWhiteBoardData) {
			onWhiteBoardData(data?.data);
			setWhiteBoardData(data?.data);
		} else if (data?.type === MeetVWInActions.onClearClick) {
			onClearClick?.();
		}
	};

	const sendMessage = (payload: object): void => {
		iframeRef.current?.contentWindow?.postMessage(payload, '*');
		window.iframeWindow = iframeRef?.current?.contentWindow;
	};

	const onLoadEnd = () => {
		clearTimeout(loadEndTimeoutRef?.current);

		loadEndTimeoutRef.current = setTimeout(() => {
			const initializeData = {
				mode: 'write',
				isMobile: isMobile,
				isSolver: isSolver
			};

			sendMessage({
				type: MeetVWOutActions.initialize,
				data: initializeData
			});
		}, 300);
		setIsLoad(false);
	};

	const onInit = (): void => {
		iframeRef.current.onload = () => {
			onLoadEnd();
		};
		setUrl('');
		setTimeout(() => {
			setUrl(process.env.DOPI_TECH_COMPONENTS_URL + `?v=${dopingTechComponentsCacheStrRef?.current}` + '/#/white-board-component');
		}, 100);
	};

	useImperativeHandle(ref, () => ({
		onRestoreData: (data: object): void => {
			sendMessage({
				type: MeetVWOutActions.restoreData,
				data: data
			});
		},
		questionImageToWhiteboard: (questionImage: string): void => {
			sendMessage({
				type: SolverActions.setQuestionImage,
				data: {
					questionImage: questionImage
				}
			});
		}
	}));

	useEffect(() => {
		iframeRef.current = document.getElementById('white-board-iframe');
		if (streamId) {
			onInit();
			window.addEventListener('message', onMessage);
		}

		return () => {
			setUrl('');
			window.removeEventListener('message', onMessage);
		};
	}, [streamId]);

	useEffect(() => {
		if (dopingTechComponentsCacheStr) {
			dopingTechComponentsCacheStrRef.current = dopingTechComponentsCacheStr;
		}
	}, [dopingTechComponentsCacheStr]);

	useEffect(() => {
		if (isInitialized) {
			const storedWhiteBoardLastData = localStorage.getItem('@whiteBoardLastData');
			const _storedWhiteBoardLastData = jsonTryParse(storedWhiteBoardLastData);

			if (_storedWhiteBoardLastData?.streamId === streamId) {
				if (_storedWhiteBoardLastData?.whiteBoardData) {
					sendMessage({
						type: MeetVWOutActions.restoreData,
						data: _storedWhiteBoardLastData?.whiteBoardData
					});
				}
			} else {
				localStorage.removeItem('@whiteBoardLastData');
			}
		}
	}, [isInitialized, streamId]);

	useEffect(() => {
		const handleBeforeUnload = () => {
			if (whiteBoardData) {
				const storedWhiteBoardLastData = {
					streamId,
					whiteBoardData
				};

				localStorage.setItem('@whiteBoardLastData', JSON.stringify(storedWhiteBoardLastData));
			}
		};

		window.addEventListener('beforeunload', handleBeforeUnload);

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [whiteBoardData, streamId]);

	return (
		<>
			<div className={ClassNames('white-board-wv-container')}>
				<iframe
					id="white-board-iframe"
					className="white-board-iframe"
					src={url}
					allow="autoplay; camera; microphone; display-capture"
					allowFullScreen={true}
					scrolling={'no'}
				/>
				{isLoad && <LoadingSpinner loadingText={'Yükleniyor'} />}
			</div>
		</>
	);
});

export default WhiteBoardWv;
